const FALLBACK = "Sorry, we're having trouble generating a summary for this property. Please try again later."
const TIMEOUT_MS = 30000
const RETRY_WINDOW_MS = 5000
const SENTINEL_ERROR = '__SSE_ERROR__'
const SENTINEL_INTERNAL = '__INTERNAL_ERROR__'

export function buildUrl({ host, propertyId, encryptedPrompt }) {
  return `${host}/ai_snapshot?trace_id=property-id-${propertyId}` +
         `&snaptrip_property_details_tool_id=${propertyId}` +
         `&prompt=${encodeURIComponent(encryptedPrompt)}`
}

// Called from Vue mounted(). Adopts the prebooted connection or starts a new one.
// onUpdate({ text, status }) — status is 'streaming' | 'done' | 'error'.
// Returns { close } for cleanup in beforeDestroy.
export function start({ host, propertyId, encryptedPrompt, preopened, onUpdate }) {
  let text = ''
  let retried = false
  let done = false
  const startedAt = Date.now()
  let es = preopened ? preopened.source : null
  let timeoutId

  const openFresh = () => new EventSource(buildUrl({ host, propertyId, encryptedPrompt }))

  const close = () => {
    done = true
    clearTimeout(timeoutId)
    try { if (es) es.close() } catch (_) {}
  }

  const fail = (reason, ctx = {}) => {
    if (done) return
    console.error(`AI summary SSE: ${reason}`, { ...ctx, streamedSoFar: text })
    onUpdate({ text: FALLBACK, status: 'error' })
    close()
  }

  const resetTimeout = () => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fail('timeout', { timeoutMs: TIMEOUT_MS }), TIMEOUT_MS)
  }

  const tryRetry = (reason) => {
    if (retried || Date.now() - startedAt >= RETRY_WINDOW_MS) return false
    retried = true
    console.warn(`AI summary SSE: ${reason}, retrying`)
    try { if (es) es.close() } catch (_) {}
    es = openFresh()
    resetTimeout()
    attach()
    return true
  }

  const handleFrame = (raw) => {
    if (done) return

    if (raw === SENTINEL_ERROR) {
      if (tryRetry('connection error')) return
      return fail('connection error')
    }
    if (raw.includes(SENTINEL_INTERNAL)) {
      if (tryRetry('internal error')) return
      return fail('internal error')
    }

    let data
    try { data = JSON.parse(raw) } catch (_) { return fail('JSON parse error', { raw }) }

    if (data.type === 'delta') {
      text += data.text
      if (text.includes(SENTINEL_INTERNAL)) {
        if (tryRetry('internal error in streamed text')) return
        return fail('internal error in streamed text')
      }
      onUpdate({ text, status: 'streaming' })
    } else if (data.type === 'done') {
      onUpdate({ text, status: 'done' })
      close()
    } else if (data.type === 'error') {
      fail('stream error event')
    }
  }

  const attach = () => {
    es.addEventListener('message', (e) => handleFrame(e.data))
    es.addEventListener('error', () => fail('EventSource connection error', { readyState: es.readyState }))
  }

  if (!es) {
    console.error('AI summary SSE: no prebooted stream available')
    onUpdate({ text: FALLBACK, status: 'error' })
    return { close: () => {} }
  }
  resetTimeout()

  // Drain buffered frames from the preboot phase (es may be replaced by tryRetry)
  const prebootBuf = preopened && preopened.buffer ? preopened.buffer : []
  for (const raw of prebootBuf) {
    handleFrame(raw)
    if (done) return { close }
  }

  // Skip attach if tryRetry already wired up the new EventSource
  if (!retried) attach()

  return { close }
}
